import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Folder, FileText, Image as ImageIcon, Download, MessageSquare, Mail } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-ukgm-gold/25 p-5 flex items-center gap-4 card-lift" data-testid={testid}>
      <div className="w-12 h-12 rounded-xl bg-ukgm-blue text-ukgm-gold flex items-center justify-center"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-xs uppercase tracking-widest text-ukgm-slate font-bold">{label}</div>
        <div className="font-serif text-2xl text-ukgm-blue font-semibold">{value ?? '—'}</div>
      </div>
    </div>
  );
}

export default function AdminOverview() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ['admin-overview'], queryFn: async () => (await api.get('/admin/overview')).data });

  return (
    <div data-testid="admin-overview-page">
      <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">{t('admin.overview')}</div>
      <h1 className="mt-2 font-serif text-3xl md:text-4xl text-ukgm-blue font-semibold">{t('admin.welcome')}, {user?.name || 'Admin'}</h1>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat icon={Folder} label={t('admin.count_projects')} value={data?.projects} testid="stat-projects" />
        <Stat icon={FileText} label={t('admin.count_blog')} value={data?.blog_posts} testid="stat-blog" />
        <Stat icon={ImageIcon} label={t('admin.count_gallery')} value={data?.gallery_items} testid="stat-gallery" />
        <Stat icon={Download} label={t('admin.count_media')} value={data?.media_assets} testid="stat-media" />
        <Stat icon={MessageSquare} label={t('admin.count_messages')} value={data?.messages} testid="stat-messages" />
        <Stat icon={Mail} label={t('admin.count_newsletter')} value={data?.newsletter} testid="stat-newsletter" />
      </div>

      <div className="mt-10 bg-white rounded-2xl border border-ukgm-gold/25 p-6">
        <h2 className="font-serif text-xl text-ukgm-blue font-semibold">Quick tips</h2>
        <ul className="mt-3 text-sm text-ukgm-slate space-y-2 list-disc pl-5">
          <li>Manage projects, blogs, gallery and media from the left sidebar.</li>
          <li>All content supports 4 languages — fill in what you have; missing translations fall back to English.</li>
          <li>Public contact form submissions arrive under <b>Messages</b>.</li>
        </ul>
      </div>
    </div>
  );
}
